import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AuthSplitLayout, { authStyles as styles } from "../../components/auth/AuthSplitLayout";
import {
  AuthField,
  AuthFormError,
  AuthPasswordField,
} from "../../components/auth/AuthFormFields";
import { resolveAuthVariant, signupAllowed } from "../../lib/authPortal";

const MIN_PASSWORD = 12;
const TERMS_URL = "https://premierlab.ai/terms";
const PRIVACY_URL = "https://premierlab.ai/privacy";

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";
  const search =
    typeof window !== "undefined" ? window.location.search : "";
  const variant = useMemo(
    () => resolveAuthVariant(hostname, search),
    [hostname, search]
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!signupAllowed(hostname, search)) {
    return <Navigate to="/login" replace data-testid="admin-signup-redirect" />;
  }

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = "Enter your full name.";
    if (!email.trim()) next.email = "Enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = "Enter a valid email address.";
    }
    if (!password) next.password = "Create a password.";
    else if (password.length < MIN_PASSWORD) {
      next.password = `Use at least ${MIN_PASSWORD} characters.`;
    }
    if (!confirm) next.confirm = "Confirm your password.";
    else if (confirm !== password) {
      next.confirm = "Passwords do not match.";
    }
    if (!agreed) next.agreed = "Please agree to the Terms and Privacy Policy.";
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!validate()) return;
    setBusy(true);
    try {
      await register({
        email: email.trim(),
        password,
        name: name.trim(),
        termsAccepted: agreed,
      });
      nav("/dashboard", { replace: true });
    } catch (e2) {
      setErr(e2.message || "Could not create your account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthSplitLayout
      variant={variant}
      eyebrow="Start creating"
      title="Create your free PremierLab account"
      subtitle="Start building with PremierLab and explore the tools available in your workspace."
      formTestId="user-signup-panel"
    >
      <form onSubmit={submit} data-testid="user-signup-form" noValidate>
        <AuthFormError message={err} testId="user-signup-error" />

        <AuthField
          id="signup-name"
          label="Full name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your full name"
          autoComplete="name"
          autoFocus
          error={fieldErrors.name}
          testId="user-signup-name"
          required
        />

        <AuthField
          id="signup-email"
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          error={fieldErrors.email}
          testId="user-signup-email"
          required
        />

        <AuthPasswordField
          id="signup-password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Create a strong password"
          autoComplete="new-password"
          helper="Use at least 12 characters."
          error={fieldErrors.password}
          testId="user-signup-password"
          toggleTestId="user-signup-password-toggle"
          required
        />

        <AuthPasswordField
          id="signup-confirm"
          label="Confirm password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Repeat your password"
          autoComplete="new-password"
          error={fieldErrors.confirm}
          testId="user-signup-confirm"
          toggleTestId="user-signup-confirm-toggle"
          required
        />

        <label className={styles.checkRow} htmlFor="signup-terms">
          <input
            id="signup-terms"
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            aria-invalid={fieldErrors.agreed ? "true" : "false"}
            data-testid="user-signup-terms"
          />
          <span>
            I agree to the{" "}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            .
          </span>
        </label>
        {fieldErrors.agreed && (
          <div className={styles.checkError} role="alert" data-testid="user-signup-terms-error">
            {fieldErrors.agreed}
          </div>
        )}

        <button
          className={styles.primary}
          data-testid="user-signup-submit"
          disabled={busy}
          type="submit"
        >
          {busy ? "Creating account…" : "Create free account"}
        </button>

        <div className={styles.below} data-testid="user-signup-signin-link">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>

        <div className={styles.legal} data-testid="user-signup-legal">
          No credit card required. Your account is protected by PremierLab
          security controls.
        </div>
      </form>
    </AuthSplitLayout>
  );
}
